import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import {
  Box, Typography, Paper, Tabs, Tab, Grid, Table, TableBody, TableRow, TableCell, Button, CircularProgress,
  Chip, FormControl, InputLabel, Select, MenuItem, TextField, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { ArrowBack, Edit, Download, Visibility, Block, Payment } from '@mui/icons-material';

const AdminAppointmentDetailView = () => {
  const { appointmentId } = useParams();
  const { adminUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [appointment, setAppointment] = useState(null);
  const [prescription, setPrescription] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      if (!adminUser) {
        setError('Please log in as an admin.');
        navigate('/admin');
        return;
      }

      try {
        const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
        const appointmentRes = await api.get(`/admin/appointments/${appointmentId}`, config);
        const prescriptionRes = await api.get(`/admin/prescriptions/appointment/${appointmentId}`, config).catch(() => ({ data: null }));

        setAppointment(appointmentRes.data);
        setPrescription(prescriptionRes.data);
        setStatus(appointmentRes.data.status);
        setNotes(appointmentRes.data.adminNotes || '');
        setRefundAmount(calculateRefundAmount(appointmentRes.data.totalFee).toFixed(2));
      } catch (err) {
        setError(err.response?.status === 404 ? 'Appointment not found.' : 'Failed to load details: ' + (err.response?.data?.details || err.message));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [appointmentId, adminUser, navigate]);

  const calculateRefundAmount = (totalFee) => Math.max(totalFee - 50, 0); // Ensure refund doesn't go negative

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const handleStatusUpdate = async () => {
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      const updatedAppointment = await api.put(`/admin/appointments/${appointmentId}`, { status, adminNotes: notes }, config);
      setAppointment(updatedAppointment.data);
    } catch (err) {
      setError('Failed to update status or notes.');
    }
  };

  const handleRefund = async () => {
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      const refundData = { refundAmount: parseFloat(refundAmount) };
      await api.post(`/admin/payments/refund/${appointmentId}`, refundData, config);
      setAppointment({
        ...appointment,
        paymentStatus: 'refunded',
        refundStatus: 'Processed',
        refundDetails: { amount: parseFloat(refundAmount), status: 'Processed', createdAt: new Date() }
      });
      setRefundDialogOpen(false);
    } catch (err) {
      setError('Failed to process refund: ' + (err.response?.data?.details || err.message));
    }
  };

  const handleBack = () => navigate(`/admin/patients/${appointment?.patientId?._id || appointment?.patientId}`);
  const viewPatient = () => navigate(`/admin/patients/${appointment?.patientId?._id || appointment?.patientId}`);
  const viewDoctor = () => navigate(`/admin/doctors/${appointment?.doctorId?._id || appointment?.doctorId}`);
  const viewPatientAppointments = () => navigate(`/admin/patients/${appointment?.patientId?._id || appointment?.patientId}`);
  const viewDoctorAppointments = () => navigate(`/admin/doctors/${appointment?.doctorId?._id || appointment?.doctorId}/appointments`);

  if (isLoading) return <Box display="flex" justifyContent="center" alignItems="center" height="100vh"><CircularProgress /></Box>;
  if (error) return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" color="error">{error}</Typography>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/patients')} sx={{ mt: 2 }}>Back to Patients</Button>
    </Box>
  );
  if (!appointment) return null;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          overflowX: { xs: 'auto', md: 'hidden' },
          whiteSpace: { xs: 'nowrap', md: 'normal' },
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pb: { xs: 1, md: 0 },
        }}
      >
        <Typography variant="h4">
          Appointment {appointment._id} - {appointment.patientName} with {appointment.doctorName || 'Unknown'}
        </Typography>
        <Chip
          label={appointment.status}
          color={appointment.status === 'completed' ? 'success' : appointment.status === 'canceled' ? 'error' : 'warning'}
          sx={{ flexShrink: 0 }}
        />
      </Box>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ overflowX: { xs: 'auto', md: 'hidden' }, pb: { xs: 1, md: 0 } }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{ mb: 3, minWidth: { xs: 600, md: 'auto' } }} // Ensure tabs are wide enough to scroll on mobile
          >
            <Tab label="General Info" />
            <Tab label="Patient Details" />
            <Tab label="Doctor Details" />
            <Tab label="Payment" />
            <Tab label="Records" />
            <Tab label="Actions" />
          </Tabs>
        </Box>

        {tabValue === 0 && (
          <Box sx={{ overflowX: { xs: 'auto', md: 'hidden' }, pb: { xs: 1, md: 0 } }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6">Appointment Information</Typography>
                <Table sx={{ minWidth: { xs: 600, md: 'auto' } }}>
                  <TableBody>
                    <TableRow><TableCell><strong>ID:</strong></TableCell><TableCell>{appointment._id}</TableCell></TableRow>
                    <TableRow><TableCell><strong>Booking Date:</strong></TableCell><TableCell>{new Date(appointment.createdAt).toLocaleString()}</TableCell></TableRow>
                    <TableRow><TableCell><strong>Original Date:</strong></TableCell><TableCell>{new Date(appointment.date).toLocaleString()}</TableCell></TableRow>
                    {appointment.status === 'rescheduled' && (
                      <>
                        <TableRow><TableCell><strong>Rescheduled Date:</strong></TableCell><TableCell>{new Date(appointment.rescheduledDate).toLocaleString()}</TableCell></TableRow>
                        <TableRow><TableCell><strong>Reschedule Reason:</strong></TableCell><TableCell>{appointment.rescheduleReason || 'N/A'}</TableCell></TableRow>
                      </>
                    )}
                    <TableRow><TableCell><strong>Symptoms:</strong></TableCell><TableCell>{appointment.symptoms || 'N/A'}</TableCell></TableRow>
                    <TableRow><TableCell><strong>Status:</strong></TableCell><TableCell>{appointment.status}</TableCell></TableRow>
                    {appointment.status === 'rejected' && (
                      <TableRow><TableCell><strong>Rejection Reason:</strong></TableCell><TableCell>{appointment.rejectReason || 'N/A'}</TableCell></TableRow>
                    )}
                    {appointment.status === 'canceled' && (
                      <TableRow><TableCell><strong>Cancellation Reason:</strong></TableCell><TableCell>{appointment.cancellationReason || 'N/A'}</TableCell></TableRow>
                    )}
                    <TableRow><TableCell><strong>Prescription Status:</strong></TableCell><TableCell>{appointment.prescriptionStatus || 'Pending'}</TableCell></TableRow>
                    <TableRow><TableCell><strong>Call Duration:</strong></TableCell><TableCell>{appointment.callDuration || 'N/A'}</TableCell></TableRow>
                    <TableRow><TableCell><strong>Completion Time:</strong></TableCell><TableCell>{appointment.completedAt ? new Date(appointment.completedAt).toLocaleString() : 'N/A'}</TableCell></TableRow>
                    <TableRow><TableCell><strong>Admin Notes:</strong></TableCell><TableCell>{appointment.adminNotes || 'None'}</TableCell></TableRow>
                  </TableBody>
                </Table>
              </Grid>
              {prescription && (
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 2 }}>Prescription Details</Typography>
                  <Table sx={{ minWidth: { xs: 600, md: 'auto' } }}>
                    <TableBody>
                      <TableRow><TableCell><strong>ID:</strong></TableCell><TableCell>{prescription._id}</TableCell></TableRow>
                      <TableRow><TableCell><strong>Medications:</strong></TableCell><TableCell>
                        {prescription.medications?.length > 0 ? prescription.medications.map(m => `${m.name} (${m.dosage}, ${m.frequency}, ${m.duration})`).join(', ') : 'None'}
                      </TableCell></TableRow>
                      <TableRow><TableCell><strong>Instructions:</strong></TableCell><TableCell>{prescription.instructions || 'N/A'}</TableCell></TableRow>
                      <TableRow><TableCell><strong>PDF Path:</strong></TableCell><TableCell>{prescription.pdfPath || 'N/A'}</TableCell></TableRow>
                    </TableBody>
                  </Table>
                  {prescription.pdfPath && (
                    <Button startIcon={<Download />} onClick={() => window.open(prescription.pdfPath, '_blank')} sx={{ mt: 2 }}>
                      Download Prescription PDF
                    </Button>
                  )}
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {tabValue === 1 && (
          <Box sx={{ overflowX: { xs: 'auto', md: 'hidden' }, pb: { xs: 1, md: 0 } }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6">Patient Details</Typography>
                <Table sx={{ minWidth: { xs: 600, md: 'auto' } }}>
                  <TableBody>
                    <TableRow><TableCell><strong>ID:</strong></TableCell><TableCell>{appointment.patientId?._id || appointment.patientId || 'N/A'}</TableCell></TableRow>
                    <TableRow><TableCell><strong>Name:</strong></TableCell><TableCell>{appointment.patientId?.name || appointment.patientName || 'N/A'}</TableCell></TableRow>
                    <TableRow><TableCell><strong>Age:</strong></TableCell><TableCell>{appointment.age || appointment.patientId?.age || 'N/A'}</TableCell></TableRow>
                    <TableRow><TableCell><strong>Gender:</strong></TableCell><TableCell>{appointment.gender || appointment.patientId?.gender || 'N/A'}</TableCell></TableRow>
                  </TableBody>
                </Table>
                <Box sx={{ mt: 2 }}>
                  <Button startIcon={<Edit />} onClick={viewPatient} sx={{ mr: 2 }}>Edit Patient Info</Button>
                  <Button startIcon={<Visibility />} onClick={viewPatientAppointments}>View Other Appointments</Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {tabValue === 2 && (
          <Box sx={{ overflowX: { xs: 'auto', md: 'hidden' }, pb: { xs: 1, md: 0 } }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6">Doctor Details</Typography>
                <Table sx={{ minWidth: { xs: 600, md: 'auto' } }}>
                  <TableBody>
                    <TableRow><TableCell><strong>ID:</strong></TableCell><TableCell>{appointment.doctorId?._id || appointment.doctorId || 'N/A'}</TableCell></TableRow>
                    <TableRow><TableCell><strong>Name:</strong></TableCell><TableCell>{appointment.doctorId?.name || appointment.doctorName || 'N/A'}</TableCell></TableRow>
                    <TableRow><TableCell><strong>Consultation Fee:</strong></TableCell><TableCell>₹{appointment.consultationFee || appointment.doctorId?.consultationFee || 0}</TableCell></TableRow>
                  </TableBody>
                </Table>
                <Box sx={{ mt: 2 }}>
                  <Button startIcon={<Edit />} onClick={viewDoctor} sx={{ mr: 2 }}>Edit Doctor Info</Button>
                  <Button startIcon={<Visibility />} onClick={viewDoctorAppointments}>View Other Appointments</Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {tabValue === 3 && (
          <Box sx={{ overflowX: { xs: 'auto', md: 'hidden' }, pb: { xs: 1, md: 0 } }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6">Payment & Earnings</Typography>
                <Table sx={{ minWidth: { xs: 600, md: 'auto' } }}>
                  <TableBody>
                    <TableRow><TableCell><strong>Consultation Fee:</strong></TableCell><TableCell>₹{appointment.consultationFee || 0}</TableCell></TableRow>
                    <TableRow><TableCell><strong>Total Amount Paid:</strong></TableCell><TableCell>₹{appointment.totalFee || 0}</TableCell></TableRow>
                    <TableRow><TableCell><strong>Refund Amount:</strong></TableCell><TableCell>₹{calculateRefundAmount(appointment.totalFee)}</TableCell></TableRow>
                    <TableRow><TableCell><strong>Payment Status:</strong></TableCell><TableCell>
                      <Chip label={appointment.paymentStatus || 'Pending'} color={appointment.paymentStatus === 'paid' ? 'success' : appointment.paymentStatus === 'refunded' ? 'error' : 'warning'} />
                    </TableCell></TableRow>
                    <TableRow><TableCell><strong>Payment ID:</strong></TableCell><TableCell>{appointment.paymentDetails?.paymentId || 'N/A'}</TableCell></TableRow>
                    <TableRow><TableCell><strong>Amount Paid:</strong></TableCell><TableCell>₹{appointment.paymentDetails?.amountPaid?.toFixed(2) || '0.00'}</TableCell></TableRow>
                    <TableRow><TableCell><strong>Payment Method:</strong></TableCell><TableCell>{appointment.paymentDetails?.method || 'N/A'}</TableCell></TableRow>
                    <TableRow><TableCell><strong>Paid At:</strong></TableCell><TableCell>
                      {appointment.paymentDetails?.paidAt ? new Date(appointment.paymentDetails.paidAt).toLocaleString() : 'N/A'}
                    </TableCell></TableRow>
                    {appointment.status === 'canceled' && appointment.paymentStatus === 'refunded' && (
                      <>
                        <TableRow><TableCell><strong>Refund Status:</strong></TableCell><TableCell>{appointment.refundStatus || 'N/A'}</TableCell></TableRow>
                        <TableRow><TableCell><strong>Refund ID:</strong></TableCell><TableCell>{appointment.refundDetails?.refundId || 'N/A'}</TableCell></TableRow>
                        <TableRow><TableCell><strong>Refund Amount:</strong></TableCell><TableCell>₹{appointment.refundDetails?.amount?.toFixed(2) || '0.00'}</TableCell></TableRow>
                        <TableRow><TableCell><strong>Refunded At:</strong></TableCell><TableCell>
                          {appointment.refundedAt ? new Date(appointment.refundedAt).toLocaleString() : 'N/A'}
                        </TableCell></TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
                <Box sx={{ mt: 2 }}>
                  <Button
                    startIcon={<Payment />}
                    onClick={() => setRefundDialogOpen(true)}
                    disabled={appointment.paymentStatus !== 'paid' || appointment.status !== 'canceled' || appointment.refundStatus === 'Processed'}
                  >
                    Process Refund
                  </Button>
                  {appointment.paymentDetails?.invoicePath && (
                    <Button startIcon={<Download />} onClick={() => window.open(appointment.paymentDetails.invoicePath, '_blank')} sx={{ ml: 2 }}>
                      Download Invoice
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {tabValue === 4 && (
          <Box sx={{ overflowX: { xs: 'auto', md: 'hidden' }, pb: { xs: 1, md: 0 } }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6">Medical Records</Typography>
                {appointment.records?.length > 0 ? (
                  <Table sx={{ minWidth: { xs: 600, md: 'auto' } }}>
                    <TableBody>
                      {appointment.records.map((record, index) => (
                        <TableRow key={index}>
                          <TableCell>{record.fileName}</TableCell>
                          <TableCell>{new Date(record.uploadDate).toLocaleString()}</TableCell>
                          <TableCell>
                            <Button startIcon={<Download />} onClick={() => window.open(record.url, '_blank')}>Download</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography>No records available</Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        )}

        {tabValue === 5 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">Appointment Actions</Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Update Status</InputLabel>
                  <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="accepted">Accepted</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                    <MenuItem value="rescheduled">Rescheduled</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="canceled">Canceled</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Admin Notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                />
                <Button startIcon={<Edit />} onClick={handleStatusUpdate}>Save Changes</Button>
                <Button startIcon={<Block />} color="error" onClick={() => navigate(`/admin/patients/ban/${appointment.patientId?._id || appointment.patientId}`)}>Block Patient</Button>
                <Button startIcon={<Block />} color="error" onClick={() => navigate(`/admin/doctors/deactivate/${appointment.doctorId?._id || appointment.doctorId}`)}>Block Doctor</Button>
              </Box>
            </Grid>
          </Grid>
        )}

        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mt: 3 }}>Back to Patient Details</Button>
      </Paper>

      <Dialog open={refundDialogOpen} onClose={() => setRefundDialogOpen(false)}>
        <DialogTitle>Process Refund</DialogTitle>
        <DialogContent>
          <Typography>
            Refund ₹{refundAmount} (Total Fee: ₹{appointment.totalFee} - ₹50 Cancellation Fee) for this appointment?
          </Typography>
          <TextField
            label="Refund Amount"
            type="number"
            value={refundAmount}
            onChange={(e) => setRefundAmount(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            inputProps={{ min: 0, max: calculateRefundAmount(appointment.totalFee) }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRefund} color="success" disabled={!refundAmount || refundAmount <= 0 || refundAmount > calculateRefundAmount(appointment.totalFee)}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminAppointmentDetailView;